/**
 * Short shareable links — deterministic pure builders. No API calls.
 * Grammar (spec §3):
 *   creator:  /s/{campCode}/{username}/{purpose}[/{subview}]
 *   page:     /s/{campCode}/p{N}/{tab}[/{pill}]  (the 5 PAGINATED_TABS only —
 *                                                 token always present, p1
 *                                                 included, for consistency
 *                                                 within that family)
 *   bare tab: /s/{campCode}/{tab}                (Details, Shopify — no
 *                                                 pill/page at all, no token)
 *   virtual:  /s/{campCode}/{slug}[/{window}]    (Reporting/Settings
 *                                                 sub-tabs — no pill/page
 *                                                 either, no token; the
 *                                                 trailing {window} segment
 *                                                 (Task 12) is emitted ONLY
 *                                                 for the two windowed
 *                                                 reporting slugs —
 *                                                 general-insights and
 *                                                 sentiment — and only when a
 *                                                 window value is present;
 *                                                 every other virtual slug
 *                                                 never gets one, even if a
 *                                                 window happens to be set on
 *                                                 the input)
 * Purpose→panel map must stay in sync with shareLinkPanelByPurpose, and
 * VIRTUAL_SUB_TAB_SLUGS with shareLinkVirtualSubTabs, in the backend
 * (internal/campaigns/share_link_handler.go).
 */
import type { CampaignSearch, CampaignTab } from '../search';

export const SHARE_PURPOSES = ['script', 'content', 'insights', 'safety', 'chat'] as const;
export type SharePurpose = (typeof SHARE_PURPOSES)[number];

// Typed against the real ?panel enum (not a bare `string`) so a typo here is
// a compile error at the definition site, not a silently-widened value that
// needs an `as` cast at every call site.
export const PANEL_BY_PURPOSE: Record<SharePurpose, NonNullable<CampaignSearch['panel']>> = {
  script: 'script-review',
  content: 'content-review',
  insights: 'insights',
  safety: 'brand-safety',
  chat: 'conversation',
};

export function purposeForPanel(panel: string): SharePurpose | null {
  switch (panel) {
    case 'script-review':
    case 'script-finalized':
      return 'script';
    case 'content-review':
    case 'content-finalized':
      return 'content';
    case 'insights':
      return 'insights';
    case 'brand-safety':
      return 'safety';
    case 'conversation':
      return 'chat';
    default:
      return null;
  }
}

const CAMP_CODE_PREFIX_LEN = 4;
const SHORT_ID_LEN = 3;
const B62 = /^[0-9a-zA-Z]+$/;

/**
 * Mirror of the Go buildCampCode (internal/campaigns/short_code.go).
 * Fallback only — prefer the campaign DTO's stored `short_code` (which is
 * collision-adjusted); this derivation covers legacy responses without it.
 */
export function buildCampCode(slug: string): string {
  const i = slug.lastIndexOf('-');
  if (i <= 0 || slug.length - i - 1 !== SHORT_ID_LEN) return '';
  const shortId = slug.slice(i + 1);
  if (!B62.test(shortId)) return '';
  const prefix = slug.slice(0, i).slice(0, CAMP_CODE_PREFIX_LEN).replace(/-+$/, '');
  if (!prefix) return '';
  return `${prefix}-${shortId}`;
}

export type ShortShareInput =
  | { kind: 'creator'; username: string; purpose: SharePurpose; sub?: string }
  | {
      kind: 'page';
      tab: CampaignTab;
      pill?: string;
      page?: number;
      sub?: string;
      /** Rows-per-page at share time — MINT-ONLY. buildShortSharePath never
       *  reads this field, so it can never leak into the shared URL; it only
       *  rides along to SharePopover's mint payload (see useCampaignShare's
       *  shareInputTail) so the admin-header page-kind share button — which has
       *  no table state of its own — can still mint a grant scoped to the
       *  active tab's live pagination (published by each roster tab into
       *  useCampaignUiStore). */
      pageSize?: number;
      /** Total page count at share time — MINT-ONLY, same rationale as pageSize. */
      totalPages?: number;
      /** Reporting-window preset (Task 12) — the CURRENT selection at share
       *  time (`'1w'|'1m'|'6m'|'full'`). Only meaningful, and only ever
       *  emitted into the URL, for the two windowed reporting virtual slugs
       *  (general-insights, sentiment) — buildShortSharePath ignores it for
       *  every other tab/slug. Rides along to the mint payload the same way
       *  pageSize/totalPages does. */
      window?: string;
      /** Active column sort at share time — MINT-ONLY, same rationale as
       *  pageSize/totalPages: buildShortSharePath never reads these, so the
       *  short URL is identical whether or not a sort is applied. Rides
       *  along to the mint payload so the resolved grant reproduces the same
       *  sorted view for a visitor. `sortBy` absent/empty = unsorted (default
       *  recency) — matches the FE's own ?sort_by absent convention. */
      sortBy?: string;
      sortDir?: 'asc' | 'desc';
    };

// Reporting and Settings have no pill/page concept — their only navigable
// state is a sub-tab, so each (tab, sub) pair gets a direct, readable URL
// segment instead of spelling out `reporting`/`settings` + `?sub=`. Must stay
// in sync with shareLinkVirtualSubTabs in the Go backend
// (internal/campaigns/share_link_handler.go).
export const VIRTUAL_SUB_TAB_SLUGS: Record<string, { tab: CampaignTab; sub: string }> = {
  'general-insights': { tab: 'reporting', sub: 'overview' },
  sentiment: { tab: 'reporting', sub: 'sentiment' },
  'audience-dynamics': { tab: 'reporting', sub: 'audienceDynamics' },
  'creator-content': { tab: 'reporting', sub: 'creatorContent' },
  conversions: { tab: 'reporting', sub: 'conversions' },
  access: { tab: 'settings', sub: 'access' },
  integrations: { tab: 'settings', sub: 'integrations' },
};

// The only two virtual slugs with a window concept at all (Task 12) — every
// other virtual slug (audience-dynamics, creator-content, conversions,
// access, integrations) never gets a trailing window segment, even if the
// input happens to carry one.
const WINDOWED_VIRTUAL_SLUGS = new Set(['general-insights', 'sentiment']);

// Reverse lookup for the ENCODE side: given a canonical (tab, sub) pair,
// return its readable slug, or null if this isn't one of the virtual
// single-segment surfaces (e.g. any roster tab, which uses pill/page instead).
function virtualSubTabSlug(tab: CampaignTab, sub: string): string | null {
  for (const [slug, v] of Object.entries(VIRTUAL_SUB_TAB_SLUGS)) {
    if (v.tab === tab && v.sub === sub) return slug;
  }
  return null;
}

// The only tabs with a genuine pill/page concept — everything else (Details,
// Shopify, and the Reporting/Settings sub-tabs above) has no pagination at
// all, so it gets no p{N} token, ever. Purely an encode-side concern — the
// Go backend's decode side doesn't need to know which tabs paginate (it just
// mechanically maps segments to query params; the FE never generates a
// token for these tabs in the first place).
export const PAGINATED_TABS = new Set<CampaignTab>(['scripts', 'content', 'creators', 'confirmed', 'live-content']);

export function buildShortSharePath(campCode: string, input: ShortShareInput): string {
  if (input.kind === 'creator') {
    const sub = input.sub ? `/${encodeURIComponent(input.sub)}` : '';
    return `/s/${campCode}/${encodeURIComponent(input.username)}/${input.purpose}${sub}`;
  }
  if (input.sub) {
    const slug = virtualSubTabSlug(input.tab, input.sub);
    if (slug) {
      // Trailing window segment (Task 12) — ONLY for the two windowed
      // reporting slugs, and ONLY when a window value is present. Emitted
      // verbatim, including 'full' as a literal path segment — never
      // omitted just because the value happens to be 'full'.
      const windowSeg =
        WINDOWED_VIRTUAL_SLUGS.has(slug) && input.window
          ? `/${encodeURIComponent(input.window)}`
          : '';
      return `/s/${campCode}/${slug}${windowSeg}`;
    }
  }
  if (!PAGINATED_TABS.has(input.tab)) {
    return `/s/${campCode}/${encodeURIComponent(input.tab)}`;
  }
  // Token is ALWAYS present (p1 included) so paginated-tab URLs stay
  // consistent regardless of which page they were shared from.
  const token = `/p${input.page && input.page > 1 ? input.page : 1}`;
  const pill = input.pill ? `/${encodeURIComponent(input.pill)}` : '';
  return `/s/${campCode}${token}/${encodeURIComponent(input.tab)}${pill}`;
}
